import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, fileUrl } from "@/lib/api";
import { StatusBadge, FAULT_STATUSES, PRIORITIES } from "@/lib/status";
import PhotoUploader from "@/components/PhotoUploader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, TriangleAlert, Trash2, FileDown } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { title: "", description: "", asset_id: "", area: "", priority: "Medium", status: "Open", photos: [] };

export default function Faults() {
  const nav = useNavigate();
  const [items, setItems] = useState(null);
  const [assets, setAssets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.listFaults({ status: filter }).then(setItems).catch(() => setItems([])), [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.listAssets().then(setAssets).catch(() => {}); }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const create = async () => {
    if (!form.title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try { await api.createFault({ ...form, asset_id: form.asset_id === "none" ? "" : form.asset_id }); toast.success("Fault logged"); setOpen(false); setForm(EMPTY); load(); }
    catch { toast.error("Failed"); } finally { setBusy(false); }
  };
  const change = async (f, patch) => { try { await api.updateFault(f.id, { ...f, ...patch }); load(); } catch { toast.error("Update failed"); } };
  const del = async (f) => { try { await api.deleteFault(f.id); toast.success("Removed"); load(); } catch { toast.error("Delete failed"); } };

  return (
    <div className="mx-auto max-w-5xl px-4 py-8 sm:px-8">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Issue tracker</span>
          <h1 className="mt-1 text-2xl font-bold tracking-tight sm:text-3xl">Faults</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button data-testid="faults-pdf-btn" onClick={() => nav("/reports/faults")} variant="outline" className="gap-2 tap-scale"><FileDown className="h-4 w-4" /> <span className="hidden sm:inline">PDF</span></Button>
          <Button data-testid="new-fault-btn" onClick={() => { setForm(EMPTY); setOpen(true); }} className="gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90"><Plus className="h-4 w-4" /> Report fault</Button>
        </div>
      </div>

      <div className="mb-5 flex gap-2">
        {["all", ...FAULT_STATUSES].map((s) => (
          <button key={s} data-testid={`fault-filter-${s}`} onClick={() => setFilter(s)} className={`rounded-full border px-3 py-1.5 text-xs font-semibold tap-scale ${filter === s ? "border-primary bg-primary/15 text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>
            {s === "all" ? "All" : s}
          </button>
        ))}
      </div>

      {items === null ? (
        <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-border py-16 text-center">
          <TriangleAlert className="mx-auto mb-3 h-10 w-10 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">No faults here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((f) => (
            <div key={f.id} data-testid={`fault-row-${f.id}`} className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <StatusBadge value={f.priority} />
                    <h3 className="truncate text-sm font-semibold text-foreground">{f.title}</h3>
                  </div>
                  <p className="mt-1 text-xs text-muted-foreground">{f.asset_name ? `${f.asset_code} · ${f.asset_name}` : (f.area || "General")}{f.reported_by ? ` · by ${f.reported_by}` : ""}</p>
                  {f.description && <p className="mt-2 text-sm text-foreground/80">{f.description}</p>}
                  {f.photos?.length > 0 && (
                    <div className="mt-2 flex gap-2">{f.photos.map((p) => <img key={p.storage_path} src={fileUrl(p.storage_path)} alt="" className="h-14 w-14 rounded-md border border-border object-cover" />)}</div>
                  )}
                </div>
                <Button variant="ghost" size="icon" onClick={() => del(f)} className="h-8 w-8 shrink-0 text-muted-foreground hover:text-destructive"><Trash2 className="h-4 w-4" /></Button>
              </div>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Select value={f.status} onValueChange={(v) => change(f, { status: v })}>
                  <SelectTrigger data-testid={`fault-status-${f.id}`} className="h-8 w-36 bg-secondary/40 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{FAULT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
                <Select value={f.priority} onValueChange={(v) => change(f, { priority: v })}>
                  <SelectTrigger className="h-8 w-32 bg-secondary/40 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
          <DialogHeader><DialogTitle>Report a fault</DialogTitle><DialogDescription>Log an issue against an asset or venue area.</DialogDescription></DialogHeader>
          <div className="space-y-4">
            <div><Label className="mb-1.5 block text-xs text-muted-foreground">Title *</Label><Input data-testid="fault-title" value={form.title} onChange={(e) => set("title", e.target.value)} className="bg-secondary/40" placeholder="e.g. Dimmer channel 4 flickering" /></div>
            <div><Label className="mb-1.5 block text-xs text-muted-foreground">Description</Label><Textarea value={form.description} onChange={(e) => set("description", e.target.value)} className="bg-secondary/40" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Priority</Label><Select value={form.priority} onValueChange={(v) => set("priority", v)}><SelectTrigger data-testid="fault-priority" className="bg-secondary/40"><SelectValue /></SelectTrigger><SelectContent>{PRIORITIES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent></Select></div>
              <div><Label className="mb-1.5 block text-xs text-muted-foreground">Status</Label><Select value={form.status} onValueChange={(v) => set("status", v)}><SelectTrigger className="bg-secondary/40"><SelectValue /></SelectTrigger><SelectContent>{FAULT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
            </div>
            <div><Label className="mb-1.5 block text-xs text-muted-foreground">Asset (optional)</Label>
              <Select value={form.asset_id || "none"} onValueChange={(v) => set("asset_id", v)}>
                <SelectTrigger data-testid="fault-asset" className="bg-secondary/40"><SelectValue placeholder="No asset" /></SelectTrigger>
                <SelectContent><SelectItem value="none">No asset</SelectItem>{assets.map((a) => <SelectItem key={a.id} value={a.id}>{a.asset_code} · {a.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="mb-1.5 block text-xs text-muted-foreground">Area (if no asset)</Label><Input value={form.area} onChange={(e) => set("area", e.target.value)} className="bg-secondary/40" placeholder="e.g. Bio box" /></div>
            <div><Label className="mb-1.5 block text-xs text-muted-foreground">Photos</Label><PhotoUploader testid="fault-photo" photos={form.photos} onChange={(p) => set("photos", p)} /></div>
            <Button data-testid="create-fault-confirm" onClick={create} disabled={busy} className="w-full gap-2 bg-primary text-primary-foreground tap-scale hover:bg-primary/90">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Log fault</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
